import React, { useState, useEffect } from 'react';
import {
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider
} from 'firebase/auth';
import { auth } from '../firebase-config';

// Google logo SVG
const GoogleLogo = () => (
  <svg width="28" height="28" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// Microsoft logo SVG
const MicrosoftLogo = () => (
  <svg width="28" height="28" viewBox="0 0 24 24">
    <path fill="#F25022" d="M1 1h10v10H1z"/>
    <path fill="#00A4EF" d="M1 13h10v10H1z"/>
    <path fill="#7FBA00" d="M13 1h10v10H13z"/>
    <path fill="#FFB900" d="M13 13h10v10H13z"/>
  </svg>
);

// Alert icon
const AlertIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="12"/>
    <line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

// Spinner
const Spinner = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
    <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
  </svg>
);

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    position: 'relative',
  },
  backgroundWrapper: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    zIndex: -1,
  },
  backgroundImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    animation: 'backgroundReveal 1.8s ease-out forwards',
    opacity: 0,
    transform: 'scale(1.02)',
  },
  card: {
    background: 'rgba(242, 233, 255, 0.95)',
    borderRadius: 32,
    padding: 40,
    width: 480,
    boxShadow: '0 0 8px 2px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(97, 60, 146, 0.15)',
    animation: 'cardReveal 1.1s ease-out 0.3s forwards',
    opacity: 0,
    transform: 'scale(0.98)',
  },
  header: {
    textAlign: 'center',
    marginBottom: 32,
  },
  gif: {
    width: '100%',
    maxWidth: 140,
    height: 'auto',
    marginBottom: 16,
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
    borderRadius: 8,
  },
  title: {
    color: '#613C92',
    fontSize: 48,
    fontWeight: 500,
    margin: '0 0 8px',
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  },
  subtitle: {
    color: '#613C92',
    fontSize: 14,
    margin: 0,
  },
  error: {
    background: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: 10,
    padding: '12px 16px',
    marginBottom: 20,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    color: '#ef4444',
    fontSize: 14,
  },
  buttons: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  ssoButton: {
    width: '100%',
    padding: 14,
    border: '1px solid rgba(97, 60, 146, 0.7)',
    borderRadius: 999,
    color: '#613C92',
    fontSize: 14,
    fontWeight: 400,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    gap: 14,
    transition: 'background-color 0.15s ease, border-color 0.15s ease',
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  },
  helpText: {
    color: '#613C92',
    fontSize: 12,
    textAlign: 'center',
    marginTop: 24,
    lineHeight: 1.5,
  },
  footer: {
    color: '#613C92',
    fontSize: 12,
    textAlign: 'center',
    marginTop: 22,
    paddingTop: 22,
    borderTop: '1px solid rgba(97, 60, 146, 0.4)',
  },
};

const keyframes = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  @keyframes backgroundReveal {
    from { opacity: 0; transform: scale(1.02); }
    to { opacity: 1; transform: scale(1); }
  }
  @keyframes cardReveal {
    from { opacity: 0; transform: scale(0.98); }
    to { opacity: 1; transform: scale(1); }
  }
`;

function Login({ error: externalError }) {
  const [error, setError] = useState(externalError);
  const [ssoLoading, setSsoLoading] = useState(null);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    if (externalError) {
      setError(externalError);
    }
  }, [externalError]);

  const handleGoogleLogin = async () => {
    setError(null);
    setSsoLoading('google');

    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      await signInWithPopup(auth, provider);
    } catch (err) {
      console.error('Google login error:', err);
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Login cancelled');
      } else if (err.code === 'auth/popup-blocked') {
        setError('Popup blocked. Please allow popups for this site');
      } else {
        setError('Google login failed. Please try again');
      }
    } finally {
      setSsoLoading(null);
    }
  };

  const handleMicrosoftLogin = async () => {
    setError(null);
    setSsoLoading('microsoft');

    try {
      const provider = new OAuthProvider('microsoft.com');
      provider.setCustomParameters({ prompt: 'select_account' });
      await signInWithPopup(auth, provider);
    } catch (err) {
      console.error('Microsoft login error:', err);
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Login cancelled');
      } else if (err.code === 'auth/popup-blocked') {
        setError('Popup blocked. Please allow popups for this site');
      } else {
        setError('Microsoft login failed. Please try again');
      }
    } finally {
      setSsoLoading(null);
    }
  };

  const getButtonStyle = (provider) => ({
    ...styles.ssoButton,
    opacity: ssoLoading && ssoLoading !== provider ? 0.5 : 1,
    cursor: ssoLoading ? 'not-allowed' : 'pointer',
    background: hovered === provider && !ssoLoading ? 'rgba(97, 60, 146, 0.05)' : 'transparent',
  });

  return (
    <>
      <style>{keyframes}</style>
      <div style={styles.container}>
        {/* Background Image */}
        <div style={styles.backgroundWrapper}>
          <img
            src="https://assistant.sofiapulse.com/f5ciMiWmXW17x7QLBZG-J"
            alt="Background"
            style={styles.backgroundImage}
          />
        </div>

        <div style={styles.card}>
          {/* Header */}
          <div style={styles.header}>
            <img
              src="https://assistant.sofiapulse.com/XkaGGSsiwV9K-yftvXL9K"
              alt="Sofia Auth"
              style={styles.gif}
            />
            <h1 style={styles.title}>Sofia Auth</h1>
            <p style={styles.subtitle}>Sign in with your organization account</p>
          </div>

          {/* Error Message */}
          {error && (
            <div style={styles.error}>
              <AlertIcon />
              <span>{error}</span>
            </div>
          )}

          {/* SSO Login Buttons */}
          <div style={styles.buttons}>
            <button
              onClick={handleGoogleLogin}
              disabled={ssoLoading !== null}
              style={getButtonStyle('google')}
              onMouseEnter={() => setHovered('google')}
              onMouseLeave={() => setHovered(null)}
            >
              {ssoLoading === 'google' ? <Spinner /> : <GoogleLogo />}
              Continue with Google
            </button>

            <button
              onClick={handleMicrosoftLogin}
              disabled={ssoLoading !== null}
              style={getButtonStyle('microsoft')}
              onMouseEnter={() => setHovered('microsoft')}
              onMouseLeave={() => setHovered(null)}
            >
              {ssoLoading === 'microsoft' ? <Spinner /> : <MicrosoftLogo />}
              Continue with Microsoft
            </button>
          </div>

          {/* Help Text */}
          <p style={styles.helpText}>
            Use your company Google or Microsoft account to sign in.
            Contact your administrator if you need access.
          </p>

          {/* Footer */}
          <p style={styles.footer}>
            Sofia Auth &bull; SofiaPulse
          </p>
        </div>
      </div>
    </>
  );
}

export default Login;
